import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { profileService } from "@/services/profileService";
import { getDashboardProfile, shouldDeferToDashboardBatch } from "@/utils/dashboardQueryCache";

const MODERATOR_ROLES = new Set(["moderator", "admin", "super_admin"]);

function isModeratorRole(role: string | undefined): boolean {
  return role !== undefined && MODERATOR_ROLES.has(role);
}

/**
 * Hook to check if the current user can moderate blog posts.
 * Shares the ["profile", userId] React Query cache with useAdminStatus / dashboard seed.
 */
export function useModeratorStatus() {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const deferToDashboard = shouldDeferToDashboardBatch(queryClient, user?.id);
  const cachedProfile = getDashboardProfile(queryClient, user?.id);

  return useQuery({
    queryKey: ["profile", user?.id],
    queryFn: () => {
      if (!user) return Promise.resolve(null);
      return profileService.fetchProfile(user.id);
    },
    enabled: !!user && !!session?.access_token && !deferToDashboard,
    ...(cachedProfile !== undefined ? { placeholderData: cachedProfile } : {}),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    select: (profile) => isModeratorRole(profile?.role),
  });
}
